/**
 * Plantafel Time Utilities - Single Source of Truth for all time calculations
 *
 * IMPORTANT: Never use `new Date("YYYY-MM-DD")` directly as it creates UTC dates.
 * Always use these utilities for consistent local timezone handling.
 */

const MINUTES_PER_DAY = 1440; // 24 * 60
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY; // 10080
const SNAP_MINUTES = 15;
const MIDNIGHT_SNAP_THRESHOLD = 7; // Minutes near midnight to snap to boundary

// Default work window (can be overridden by settings)
// IMPORTANT: Must match PlantafelService defaults to avoid position mismatches
const DEFAULT_DAY_START_MINUTES = 6 * 60; // 06:00 - matches service default
const DEFAULT_DAY_END_MINUTES = 18 * 60; // 18:00

/**
 * Creates a Date object for the start of a week from a date string.
 * Uses local timezone, NOT UTC.
 */
export function getWeekStartDateTime(weekStartStr: string): Date {
  const [year, month, day] = weekStartStr.split('-').map(Number);
  const date = new Date(year, month - 1, day, 0, 0, 0, 0);
  return date;
}

/**
 * Converts a Date to minutes from week start.
 * Returns the number of minutes since the beginning of the week.
 */
export function dateToMinutes(date: Date, weekStartDt: Date): number {
  const diffMs = date.getTime() - weekStartDt.getTime();
  return Math.round(diffMs / (1000 * 60));
}

/**
 * Converts minutes from week start back to a Date.
 * Inverse of dateToMinutes - roundtrip should preserve the value.
 */
export function minutesToDate(minutes: number, weekStartDt: Date): Date {
  return new Date(weekStartDt.getTime() + minutes * 60 * 1000);
}

/**
 * Formats a Date to ISO datetime string WITH timezone offset.
 * This ensures the backend correctly interprets the local time.
 * Example: "2024-12-23T10:00:00+01:00" for CET
 */
export function formatLocalDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const mins = date.getMinutes().toString().padStart(2, '0');

  // Get timezone offset in minutes (negative for UTC+, positive for UTC-)
  const tzOffset = -date.getTimezoneOffset();
  const tzSign = tzOffset >= 0 ? '+' : '-';
  const tzHours = Math.floor(Math.abs(tzOffset) / 60).toString().padStart(2, '0');
  const tzMins = (Math.abs(tzOffset) % 60).toString().padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${mins}:00${tzSign}${tzHours}:${tzMins}`;
}

/**
 * Formats a Date to date string (YYYY-MM-DD) in LOCAL timezone.
 */
export function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Snaps minutes to grid for START times (floors to nearest interval).
 * Also handles midnight boundary snapping.
 */
export function snapStart(minutes: number): number {
  let snapped = Math.floor(minutes / SNAP_MINUTES) * SNAP_MINUTES;
  snapped = snapNearDayBoundary(snapped);
  return snapped;
}

/**
 * Snaps minutes to grid for END times (ceils to nearest interval).
 * Also handles midnight boundary snapping.
 */
export function snapEnd(minutes: number): number {
  let snapped = Math.ceil(minutes / SNAP_MINUTES) * SNAP_MINUTES;
  snapped = snapNearDayBoundary(snapped);
  return snapped;
}

/**
 * Snaps minutes near day boundaries to exact midnight.
 * If within MIDNIGHT_SNAP_THRESHOLD of a day boundary, snaps to it.
 */
export function snapNearDayBoundary(minutes: number): number {
  const minutesInDay = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;

  // Near start of day (e.g., 00:05 -> 00:00)
  if (minutesInDay > 0 && minutesInDay <= MIDNIGHT_SNAP_THRESHOLD) {
    return minutes - minutesInDay;
  }

  // Near end of day (e.g., 23:55 -> 24:00 = next day 00:00)
  if (minutesInDay >= MINUTES_PER_DAY - MIDNIGHT_SNAP_THRESHOLD) {
    return minutes + (MINUTES_PER_DAY - minutesInDay);
  }

  return minutes;
}

/**
 * Snaps delta minutes for drag operations (rounds to nearest interval).
 */
export function snapDelta(deltaMinutes: number): number {
  const snapped = Math.round(deltaMinutes / SNAP_MINUTES) * SNAP_MINUTES;
  return snapNearDayBoundary(snapped);
}

/**
 * Clamps minutes to valid week range [0, MINUTES_PER_WEEK].
 */
export function clampMinutes(minutes: number): number {
  return Math.max(0, Math.min(MINUTES_PER_WEEK, minutes));
}

/**
 * Clamps start minutes ensuring minimum duration from end.
 */
export function clampStart(startMinutes: number, endMinutes: number, minDuration: number = SNAP_MINUTES): number {
  const clamped = clampMinutes(startMinutes);
  return Math.min(clamped, endMinutes - minDuration);
}

/**
 * Clamps end minutes ensuring minimum duration from start.
 */
export function clampEnd(endMinutes: number, startMinutes: number, minDuration: number = SNAP_MINUTES): number {
  const clamped = clampMinutes(endMinutes);
  return Math.max(clamped, startMinutes + minDuration);
}

/**
 * Calculates X position in pixels to minutes from week start.
 * Accounts for scroll offset.
 */
export function pixelToMinutes(
  clientX: number,
  containerRect: DOMRect,
  scrollLeft: number,
  totalWidth: number
): number {
  const x = (clientX - containerRect.left) + scrollLeft;
  const ratio = x / totalWidth;
  return ratio * MINUTES_PER_WEEK;
}

/**
 * Calculates minutes to X position in pixels.
 */
export function minutesToPixel(minutes: number, totalWidth: number): number {
  return (minutes / MINUTES_PER_WEEK) * totalWidth;
}

/**
 * Gets the day index (0-6) from minutes.
 */
export function getDayIndexFromMinutes(minutes: number): number {
  return Math.floor(clampMinutes(minutes) / MINUTES_PER_DAY);
}

/**
 * Gets minutes at the start of a specific day.
 */
export function getDayStartMinutes(dayIndex: number): number {
  return dayIndex * MINUTES_PER_DAY;
}

/**
 * Gets minutes at the end of a specific day.
 */
export function getDayEndMinutes(dayIndex: number): number {
  return (dayIndex + 1) * MINUTES_PER_DAY;
}

/**
 * Parses an ISO datetime string to minutes from week start.
 */
export function parseISOToMinutes(isoString: string, weekStartDt: Date): number {
  const date = new Date(isoString);
  return dateToMinutes(date, weekStartDt);
}

/**
 * Creates today's date string in local timezone.
 */
export function getTodayDateStr(): string {
  return formatLocalDate(new Date());
}

/**
 * Clamps minutes within a work window for a given day.
 * Ensures times stay within day_start and day_end boundaries.
 */
export function clampToWorkWindow(
  minutes: number,
  dayStartMinutes: number = DEFAULT_DAY_START_MINUTES,
  dayEndMinutes: number = DEFAULT_DAY_END_MINUTES
): number {
  const dayIndex = Math.floor(minutes / MINUTES_PER_DAY);
  const minutesInDay = minutes - (dayIndex * MINUTES_PER_DAY);

  // Clamp to work window
  const clampedMinutesInDay = Math.max(dayStartMinutes, Math.min(dayEndMinutes, minutesInDay));

  return dayIndex * MINUTES_PER_DAY + clampedMinutesInDay;
}

/**
 * Gets the start of the work window for a given day index.
 */
export function getWorkDayStart(
  dayIndex: number,
  dayStartMinutes: number = DEFAULT_DAY_START_MINUTES
): number {
  return dayIndex * MINUTES_PER_DAY + dayStartMinutes;
}

/**
 * Gets the end of the work window for a given day index.
 */
export function getWorkDayEnd(
  dayIndex: number,
  dayEndMinutes: number = DEFAULT_DAY_END_MINUTES
): number {
  return dayIndex * MINUTES_PER_DAY + dayEndMinutes;
}

/**
 * Snaps drop position to work window start if near the day boundary.
 * When user drops "at day start", it should be day_start, not 00:00.
 */
export function snapToWorkWindowStart(
  minutes: number,
  dayStartMinutes: number = DEFAULT_DAY_START_MINUTES,
  dayEndMinutes: number = DEFAULT_DAY_END_MINUTES
): number {
  const dayIndex = Math.floor(minutes / MINUTES_PER_DAY);
  const minutesInDay = minutes - (dayIndex * MINUTES_PER_DAY);

  // If before work window start, snap to work window start
  if (minutesInDay < dayStartMinutes) {
    return dayIndex * MINUTES_PER_DAY + dayStartMinutes;
  }

  // If after work window end, snap to next day's work window start
  if (minutesInDay > dayEndMinutes) {
    return (dayIndex + 1) * MINUTES_PER_DAY + dayStartMinutes;
  }

  // Already within work window - just snap to grid
  return snapStart(minutes);
}

/**
 * Snaps resize end position to work window end if near the day boundary.
 * When user resizes "to day end", it should be day_end, not 00:00.
 */
export function snapToWorkWindowEnd(
  minutes: number,
  dayStartMinutes: number = DEFAULT_DAY_START_MINUTES,
  dayEndMinutes: number = DEFAULT_DAY_END_MINUTES
): number {
  const dayIndex = Math.floor(minutes / MINUTES_PER_DAY);
  const minutesInDay = minutes - (dayIndex * MINUTES_PER_DAY);

  // If before work window start, use previous day's work window end
  if (minutesInDay < dayStartMinutes && dayIndex > 0) {
    return (dayIndex - 1) * MINUTES_PER_DAY + dayEndMinutes;
  }

  // If after work window end, snap to work window end
  if (minutesInDay > dayEndMinutes) {
    return dayIndex * MINUTES_PER_DAY + dayEndMinutes;
  }

  // Already within work window - just snap to grid
  return snapEnd(minutes);
}

/**
 * Gets hour markers for the work window (e.g., 07, 09, 11, 13, 15, 17).
 */
export function getHourMarkers(
  dayStartMinutes: number = DEFAULT_DAY_START_MINUTES,
  dayEndMinutes: number = DEFAULT_DAY_END_MINUTES,
  intervalHours: number = 2
): number[] {
  const markers: number[] = [];
  const startHour = Math.floor(dayStartMinutes / 60);
  const endHour = Math.ceil(dayEndMinutes / 60);

  for (let hour = startHour; hour <= endHour; hour += intervalHours) {
    markers.push(hour);
  }

  return markers;
}

/**
 * Formats minutes as HH:MM string.
 */
export function formatMinutesAsTime(totalMinutes: number): string {
  const minutesInDay = ((totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(minutesInDay / 60);
  const mins = minutesInDay % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

// Export constants for use elsewhere
export const TIME_CONSTANTS = {
  MINUTES_PER_DAY,
  MINUTES_PER_WEEK,
  SNAP_MINUTES,
  MIDNIGHT_SNAP_THRESHOLD,
  DEFAULT_DAY_START_MINUTES,
  DEFAULT_DAY_END_MINUTES,
} as const;
